import React, { useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiDollarCircle, BiImage, BiInfoCircle, BiLink, BiPlus, BiTime, BiVideo } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAppSelector } from '@/app/hook';
import { useUploadToCDNMutation } from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import TemplateCard, { TemplateCardData } from '@/components/common/TemplateCard';
import BotResponseInput from '@/components/dashboard/knowledge-bases-create/scenario/BotResponseInput';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from '@/constants/marketplace';
import { selectUserProfile } from '@/features/auth/auth.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { selectCurrentDataState, selectCurrentSectionState } from '../../create.slice';

function Step3() {
  const { t } = useTranslation();
  const {
    reset,
    register,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const currentData = useAppSelector(selectCurrentDataState);
  const currentSection = useAppSelector(selectCurrentSectionState);
  const [cdnUpload, cdnUploadResults] = useUploadToCDNMutation();
  const userProfile = useAppSelector(selectUserProfile);
  const user_identification = userProfile?.user_identification || 'user';

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = (file: File | null, field: 'promotional_video' | 'image') => {
    setValue(`data.${field}`, null);
    if (file) {
      // File size validation
      if ((field === 'image' && file.size > MAX_IMAGE_SIZE) || (field === 'promotional_video' && file.size > MAX_VIDEO_SIZE)) {
        const maxSizeText = field === 'image' ? '5MB' : '30MB';
        toast.error(t(`marketplace/step3/${field}/maxFileSize`, { max: maxSizeText }));
        return;
      }

      // File type validation
      const allowedTypes = field === 'image' ? ['image/png', 'image/jpeg', 'image/webp'] : ['video/mp4'];

      if (!allowedTypes.includes(file.type)) {
        toast.error(t(`marketplace/step3/${field}/invalidType`));
        return;
      }

      const formData = new FormData();
      formData.append('files', file, user_identification + '_' + file.name);
      formData.append(
        'data',
        JSON.stringify({
          orginalName: file.name,
          type: file.type,
          size: file.size,
        })
      );

      cdnUpload(formData)
        .unwrap()
        .then(value => {
          if (value[0]?.is_successful) {
            const path = value[0].cdnPath;
            setValue(`data.${field}`, path, { shouldValidate: true });
            toast.success(t(`marketplace/step3/${field}/uploadSuccess`));
          } else {
            toast.error(t(`marketplace/step3/${field}/uploadError`));
          }
        })
        .catch((e: any) => {
          toast.error(e.data?.message);
        });
    }
  };

  useEffect(() => {
    setValue('price', String(currentData?.price ?? ''));
    if (currentData?.data) {
      reset({
        ...currentData,
        data: {
          description: currentData.data.description || '',
          image: currentData.data.image || null,
          youtube_link: currentData.data.youtube_link || '',
          linkedin_link: currentData.data.linkedin_link || '',
        },
      });
      if (currentData?.type === 'video') {
        reset({
          ...currentData,
          data: {
            ...(currentData?.data || {}),
            promotional_video: currentData.data.promotional_video || '',
            delivery_delay: currentData.data.delivery_delay || 0,
          },
        });
      }
    }
  }, [currentData]);

  useEffect(() => {
    if (currentSection) {
      const el = document.getElementById(currentSection);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);
  return (
    <div className="tw-max-w-7xl tw-mx-auto tw-p-6 tw-font-['Lexend']">
      {/* Header */}
      <div className="tw-text-center tw-space-y-3 tw-mb-8">
        <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900">{t('marketplace/step3/header/title')}</h1>
        <p className="tw-text-lg tw-text-gray-600">{t('marketplace/step3/header/description')}</p>
      </div>

      {/* Two Column Layout */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
        {/* Main Content - Left Side */}
        <div className="lg:tw-col-span-2 tw-space-y-8">
          {/* Description Section */}
          <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4" id="description">
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiInfoCircle className="tw-w-5 tw-h-5 tw-text-blue-600" />
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/description/title')}</h2>
              <span className="tw-text-red-500">*</span>
            </div>

            <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/description/hint')}</p>

            <BannerHelper message={t('marketplace/step3/description/tip')} isReactHtmlParser={true} />

            <div className="tw-space-y-2">
              <Controller
                name="data.description"
                control={control}
                rules={{
                  required: t('marketplace/step3/description/validation/required'),
                  maxLength: {
                    value: 300,
                    message: t('marketplace/step3/description/validation/maxLength', { max: 300 }),
                  },
                }}
                render={({ field }) => (
                  <BotResponseInput
                    title={''}
                    placeholder={t('marketplace/step3/description/placeholder')}
                    setValue={(name, value) => {
                      if (typeof value === 'string' && value.length > 300) {
                        field.onChange(value.substring(0, 300));
                        toast.warn(t('marketplace/step3/description/validation/maxLength', { max: 300 }));
                        return;
                      }
                      field.onChange(value);
                    }}
                    getValues={getValues}
                    errors={errors}
                    name={'data.description'}
                    config={null}
                    showCallupFunctionsButton={false}
                    max={300}
                  />
                )}
              />
              {errors?.data?.description && (
                <div className="tw-flex tw-items-start tw-gap-2 tw-mt-2">
                  <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                  <p className="tw-text-sm tw-text-red-600">{errors.data.description.message as string}</p>
                </div>
              )}
            </div>
          </div>

          {/* Media Upload Section */}
          <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-6" id="media">
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiImage className="tw-w-5 tw-h-5 tw-text-green-600" />
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/image/title')}</h2>
              <span className="tw-text-red-500">*</span>
            </div>

            <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/image/hint')}</p>

            <BannerHelper
              message={`<strong>${t('marketplace/step3/image/specifications/title')}</strong><br/>
                <strong>${t('marketplace/step3/image/specifications/aspectRatio/label')}:</strong> ${t(
                'marketplace/step3/image/specifications/aspectRatio/value'
              )}<br/>
                <strong>${t('marketplace/step3/image/specifications/minimumSize/label')}:</strong> ${t(
                'marketplace/step3/image/specifications/minimumSize/value'
              )}<br/>
                <strong>${t('marketplace/step3/image/specifications/format/label')}:</strong> ${t('marketplace/step3/image/specifications/format/value')}<br/>
                <strong>${t('marketplace/step3/image/specifications/maxSize/label')}:</strong> ${t('marketplace/step3/image/specifications/maxSize/value')}`}
            />

            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-gap-4">
              <PSInput
                type="file"
                hidden
                accept="image/png,image/jpeg,image/webp"
                ref={fileInputRef}
                onChange={e => handleUpload(e.target.files?.[0] || null, 'image')}
              />

              <div className="tw-flex tw-flex-col tw-gap-2">
                <PSButton
                  id="btn-step3-file"
                  variant="outline"
                  onClick={e => {
                    fileInputRef.current?.click();
                  }}
                  type="button"
                  disabled={cdnUploadResults.isLoading}
                  className={`tw-flex tw-items-center tw-gap-2 ${
                    !watch('data.image') ? 'tw-border-red-300 tw-text-red-600 hover:tw-border-red-400' : 'tw-border-green-300 tw-text-green-600'
                  }`}
                >
                  {cdnUploadResults.isLoading ? <Spinner size="sm" /> : <BiPlus className="tw-w-4 tw-h-4" />}
                  {cdnUploadResults.isLoading ? t('LoadingComponent/text') : t('marketplace/step3/image/chooseButton')}
                </PSButton>

                {watch('data.image') && (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-green-600">
                    <BiImage className="tw-w-4 tw-h-4" />
                    <span>{t('marketplace/step3/image/uploaded')}</span>
                  </div>
                )}

                {!watch('data.image') && (
                  <div className="tw-flex tw-items-start tw-gap-2 tw-mt-1">
                    <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                    <p className="tw-text-sm tw-text-red-600">{t('marketplace/step3/image/validation/required')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4" id="pricing">
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiDollarCircle className="tw-w-5 tw-h-5 tw-text-yellow-600" />
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/pricing/title')}</h2>
              <span className="tw-text-red-500">*</span>
            </div>

            <BannerHelper
              message={`<strong>${t('marketplace/step3/pricing/range')}</strong>: $${currentData?.type === 'video' ? '0 - $499' : '0 - $199'}<br/>
                ${currentData?.type === 'video' ? t('marketplace/step3/pricing/videoNote') : t('marketplace/step3/pricing/textNote')}`}
            />

            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/pricing/label')}</label>
              <div className="tw-relative tw-w-40">
                <span className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-500 tw-font-medium">$</span>
                <PSInput
                  min={0}
                  max={currentData?.type === 'video' ? 499 : 199}
                  step={1}
                  {...register('price', {
                    required: t('marketplace/step3/pricing/validation/required'),
                    min: {
                      value: 0,
                      message: t('marketplace/step3/pricing/validation/minValue'),
                    },
                    max: {
                      value: currentData?.type === 'video' ? 499 : 199,
                      message: t('marketplace/step3/pricing/validation/maxValue', {
                        max: currentData?.type === 'video' ? 499 : 199,
                      }),
                    },
                    validate: value => {
                      if (value === '' || value === null || value === undefined) {
                        return t('marketplace/step3/pricing/validation/required');
                      }
                      const numValue = parseFloat(value);
                      if (isNaN(numValue)) {
                        return t('marketplace/step3/pricing/validation/invalidNumber');
                      }
                      return true;
                    },
                  })}
                  placeholder={t('marketplace/step3/pricing/placeholder')}
                  type={'number'}
                  className={`tw-border tw-border-solid tw-border-gray-300 tw-pl-8 tw-pr-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                    errors.price
                      ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                      : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                  }`}
                />
              </div>
              {errors?.price && (
                <div className="tw-flex tw-items-start tw-gap-2 tw-mt-2">
                  <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                  <p className="tw-text-sm tw-text-red-600">{errors.price.message as string}</p>
                </div>
              )}
            </div>
          </div>
          {/* Video-specific fields */}
          {currentData?.type === 'video' && (
            <div className="tw-space-y-6">
              {/* Delivery delay */}
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4" id="delivery">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiTime className="tw-w-5 tw-h-5 tw-text-purple-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/delivery/title')}</h2>
                  <span className="tw-text-red-500">*</span>
                </div>

                <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/delivery/hint')}</p>

                <BannerHelper message={t('marketplace/step3/delivery/tip')} isReactHtmlParser={true} />

                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/delivery/label')}</label>
                  <Controller
                    name="data.delivery_delay"
                    control={control}
                    render={({ field }) => (
                      <PSInput
                        {...field}
                        value={field.value ?? ''}
                        type="number"
                        min={1}
                        max={14}
                        placeholder={t('marketplace/step3/delivery/placeholder')}
                        className={`tw-border tw-border-solid tw-border-gray-300 tw-w-32 tw-px-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                          errors.data?.delivery_delay
                            ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                            : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                        }`}
                      />
                    )}
                  />
                  <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{t('marketplace/step3/delivery/note')}</p>
                  {errors.data?.delivery_delay && (
                    <div className="tw-flex tw-items-start tw-gap-2 tw-mt-2">
                      <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                      <p className="tw-text-sm tw-text-red-600">{errors.data.delivery_delay.message as string}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Promo video upload */}
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiVideo className="tw-w-5 tw-h-5 tw-text-red-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/video/title')}</h2>
                  <span className="tw-text-red-500">*</span>
                </div>

                <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/video/hint')}</p>

                <BannerHelper
                  message={`<strong>${t('marketplace/step3/video/specifications/title')}</strong><br/>
                    <strong>${t('marketplace/step3/video/specifications/aspectRatio/label')}:</strong> ${t(
                    'marketplace/step3/video/specifications/aspectRatio/value'
                  )}<br/>
                    <strong>${t('marketplace/step3/video/specifications/resolution/label')}:</strong> ${t(
                    'marketplace/step3/video/specifications/resolution/value'
                  )}<br/>
                    <strong>${t('marketplace/step3/video/specifications/duration/label')}:</strong> ${t(
                    'marketplace/step3/video/specifications/duration/value'
                  )}<br/>
                    <strong>${t('marketplace/step3/video/specifications/format/label')}:</strong> ${t(
                    'marketplace/step3/video/specifications/format/value'
                  )}<br/>
                    <strong>${t('marketplace/step3/video/specifications/maxSize/label')}:</strong> ${t(
                    'marketplace/step3/video/specifications/maxSize/value'
                  )}`}
                />

                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-gap-4">
                  <PSInput
                    type="file"
                    hidden
                    accept="video/mp4"
                    ref={videoInputRef}
                    onChange={e => handleUpload(e.target.files?.[0] || null, 'promotional_video')}
                  />

                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <PSButton
                      id="btn-step3-video"
                      variant="outline"
                      onClick={e => {
                        videoInputRef.current?.click();
                      }}
                      type="button"
                      disabled={cdnUploadResults.isLoading}
                      className={`tw-flex tw-items-center tw-gap-2 ${
                        !watch('data.promotional_video') ? 'tw-border-red-300 tw-text-red-600 hover:tw-border-red-400' : 'tw-border-green-300 tw-text-green-600'
                      }`}
                    >
                      {cdnUploadResults.isLoading ? <Spinner size="sm" /> : <BiPlus className="tw-w-4 tw-h-4" />}
                      {cdnUploadResults.isLoading ? t('LoadingComponent/text') : t('marketplace/step3/video/chooseButton')}
                    </PSButton>

                    {watch('data.promotional_video') && (
                      <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-green-600">
                        <BiVideo className="tw-w-4 tw-h-4" />
                        <span>{t('marketplace/step3/video/uploaded')}</span>
                      </div>
                    )}

                    {!watch('data.promotional_video') && (
                      <div className="tw-flex tw-items-start tw-gap-2 tw-mt-1">
                        <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                        <p className="tw-text-sm tw-text-red-600">{t('marketplace/step3/video/validation/required')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Optional links */}
          <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-6" id="links">
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiLink className="tw-w-5 tw-h-5 tw-text-indigo-600" />
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/links/title')}</h2>
              <span className="tw-text-gray-400">({t('marketplace/step3/links/optional')})</span>
            </div>

            <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/links/hint')}</p>

            <div className="tw-space-y-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/links/youtube/label')}</label>
                <Controller
                  name="data.youtube_link"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?(youtube\.com\/|youtu\.be\/).+/,
                      message: t('marketplace/step3/links/youtube/validation/invalid'),
                    },
                  }}
                  render={({ field }) => (
                    <PSInput
                      {...field}
                      value={field.value ?? ''}
                      placeholder={t('marketplace/step3/links/youtube/placeholder')}
                      className={`tw-border tw-border-solid tw-border-gray-300 tw-px-4 tw-py-3 tw-text-base tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                        errors.data?.youtube_link
                          ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                          : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                      }`}
                    />
                  )}
                />
                {errors.data?.youtube_link && (
                  <div className="tw-flex tw-items-start tw-gap-2 tw-mt-2">
                    <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                    <p className="tw-text-sm tw-text-red-600">{errors.data.youtube_link.message as string}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">{t('marketplace/step3/links/linkedin/label')}</label>
                <Controller
                  name="data.linkedin_link"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?linkedin\.com\/.+/,
                      message: t('marketplace/step3/links/linkedin/validation/invalid'),
                    },
                  }}
                  render={({ field }) => (
                    <PSInput
                      {...field}
                      value={field.value ?? ''}
                      placeholder={t('marketplace/step3/links/linkedin/placeholder')}
                      className={`tw-border tw-border-solid tw-border-gray-300 tw-px-4 tw-py-3 tw-text-base tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-opacity-50 ${
                        errors.data?.linkedin_link
                          ? 'tw-border-red-500 focus:tw-border-red-500 focus:tw-ring-red-500'
                          : 'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500'
                      }`}
                    />
                  )}
                />
                {errors.data?.linkedin_link && (
                  <div className="tw-flex tw-items-start tw-gap-2 tw-mt-2">
                    <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" />
                    <p className="tw-text-sm tw-text-red-600">{errors.data.linkedin_link.message as string}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Sticky Preview */}
        <div className="lg:tw-col-span-1">
          <div className="tw-sticky tw-top-6">
            <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4 tw-shadow-lg">
              <div className="tw-flex tw-items-center tw-gap-2">
                <BiImage className="tw-w-5 tw-h-5 tw-text-indigo-600" />
                <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step3/preview/title')}</h2>
              </div>
              <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step3/preview/hint')}</p>

              <div className="tw-w-full">
                <TemplateCard
                  item={
                    {
                      name: currentData?.name || 'Package Name',
                      price: parseFloat(watch('price')) || 0,
                      type: currentData?.type || 'chatbot',
                      version: currentData?.version,
                      data: {
                        description: watch('data.description') || '',
                        image: watch('data.image') || '',
                        youtube_link: watch('data.youtube_link') || '',
                        linkedin_link: watch('data.linkedin_link') || '',
                        delivery_delay: watch('data.delivery_delay') ? parseInt(watch('data.delivery_delay')) : undefined,
                        promotional_video: watch('data.promotional_video') || undefined,
                      },
                      publisher: {
                        name: '',
                        icon: '',
                      },
                      downloadCount: 120,
                      configuration: {
                        bots: currentData.configuration.bots.map((bot: any) => {
                          return bot.name;
                        }),
                      },
                    } as TemplateCardData
                  }
                  isSelected={false}
                  onClick={() => {
                    // Do nothing in preview mode - just a no-op function
                  }}
                  disableAction={true}
                  canCreateMore={false}
                  isInEditMode={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WrappedErrorBoundary(Step3);
